import _Tooltip from "@mui/material/Tooltip";
import _IconButton from "@mui/material/IconButton";
import _Close from "@mui/icons-material/Close";
import _Check from "@mui/icons-material/Check";
import _Typography from "@mui/material/Typography";
import _Box from "@mui/material/Box";
import _useMediaQuery from "@mui/material/useMediaQuery";
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import trophy from '../assets/images/trophy.svg';
import { useCancellablePromise } from '../hooks';
import { GeogroupService } from '../services';
import { Button as CustomButton } from './button';
const now = moment();
const defaultColor = '#FCCE2C';
export function HighlightedEventCard({
  Button: _Button,
  hasGeogroups,
  partner,
  geogroup,
  highlightedEvent,
  highlightedEventChallenge,
  displayButtonProps,
  mustAskAdmin,
  requestSent,
  onJoined,
  setRequestSent,
  onClose,
  onSeeEventOffer,
  onStartEventOnboarding,
  notJoinedDisplayButtonProps
}) {
  const _theme = useTheme();
  const [theme, setTheme] = useState(_theme);
  const [joiningEvent, setJoiningEvent] = useState(false);
  const {
    t
  } = useTranslation();
  const {
    enqueueSnackbar
  } = useSnackbar();
  const smallDevice = _useMediaQuery(theme.breakpoints.down('md'));
  const {
    cancellablePromise,
    cancelPromises
  } = useCancellablePromise();
  useEffect(() => {
    return () => cancelPromises();
  }, []);
  useEffect(() => {
    if (highlightedEvent?.primaryColor) {
      setTheme(createTheme({
        ..._theme,
        palette: {
          primary: {
            main: highlightedEvent.primaryColor
          },
          purple: {
            main: '#905EFA',
            contrastText: '#FFF'
          },
          red: {
            main: '#DD428D',
            contrastText: '#fff'
          }
        }
      }));
    }
  }, [highlightedEvent]);
  async function handleEventJoin() {
    if (!geogroup || !highlightedEvent) return;
    setJoiningEvent(true);
    try {
      const challenge = await cancellablePromise(GeogroupService.addChallenge({
        code: partner?.code,
        geoGroupId: geogroup.id
      }, {
        event: highlightedEvent.id
      }));
      enqueueSnackbar(t('commons.highlighted_event_card.success'));
      onJoined?.(challenge);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        enqueueSnackbar(t('commons.highlighted_event_card.server_error'), {
          variant: 'error'
        });
        setJoiningEvent(false);
      }
    }
  }
  async function handleEventAskToJoin() {
    if (!geogroup || !highlightedEvent || requestSent !== false) return;
    setJoiningEvent(true);
    try {
      await cancellablePromise(GeogroupService.askToJoinEvent({
        geoGroupId: geogroup.id,
        eventId: highlightedEvent.id
      }));
      try {
        localStorage.setItem(`highlighted_event_request_to_join_sent_${geogroup.id}_${highlightedEvent.id}`, 'true');
      } catch {
        console.error('localStorage access is denied');
      }
      enqueueSnackbar(t('commons.highlighted_event_card.success_asking'));
      setRequestSent?.(true);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        enqueueSnackbar(t('commons.highlighted_event_card.server_error_asking'), {
          variant: 'error'
        });
        setJoiningEvent(false);
      }
    }
  }
  if (!highlightedEvent || highlightedEvent.endDate.isBefore(now) || highlightedEventChallenge === undefined) return /*#__PURE__*/React.createElement(React.Fragment, null);
  const Button = _Button || CustomButton;
  const row = !smallDevice;
  const bannerColor = highlightedEvent.primaryColor || defaultColor;
  const {
    textPrimaryColor,
    secondaryColor,
    textSecondaryColor
  } = highlightedEvent;
  const primaryCtaStyle = secondaryColor ? {
    backgroundColor: secondaryColor,
    color: textSecondaryColor || undefined
  } : undefined;
  const secondaryCtaStyle = secondaryColor ? {
    color: secondaryColor
  } : undefined;
  return /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: theme
  }, /*#__PURE__*/React.createElement(_Box, {
    bgcolor: bannerColor,
    borderRadius: 2,
    flexShrink: 0,
    overflow: "hidden",
    position: "relative",
    sx: ({
      palette
    }) => ({
      color: palette.primary.contrastText
    }),
    width: "100%"
  }, /*#__PURE__*/React.createElement(_Box, {
    left: 0,
    position: "absolute",
    right: 0,
    top: 0,
    zIndex: 1
  }, /*#__PURE__*/React.createElement(TopIllustration, {
    color: bannerColor
  })), /*#__PURE__*/React.createElement(_Box, {
    bottom: 0,
    display: "flex",
    justifyContent: "flex-end",
    left: 0,
    position: "absolute",
    right: 0,
    zIndex: 1
  }, /*#__PURE__*/React.createElement(BottomIllustration, {
    color: bannerColor
  })), /*#__PURE__*/React.createElement(_Box, {
    alignItems: row ? 'center' : 'flex-start',
    display: "flex",
    flexDirection: row ? 'row' : 'column',
    gap: 2,
    padding: 2,
    position: "relative",
    zIndex: 2
  }, highlightedEvent.icon ? /*#__PURE__*/React.createElement("img", {
    src: highlightedEvent.icon,
    style: {
      flexShrink: 0
    },
    width: row ? 100 : 60
  }) : /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    bgcolor: "#fff",
    borderRadius: 2,
    display: "flex",
    flexShrink: 0,
    height: 50,
    justifyContent: "center",
    width: 100
  }, /*#__PURE__*/React.createElement("img", {
    src: trophy,
    width: 50
  })), /*#__PURE__*/React.createElement(_Box, {
    alignItems: "flex-start",
    display: "flex",
    flexGrow: 1,
    flexWrap: "wrap",
    gap: 1
  }, /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1
  }, /*#__PURE__*/React.createElement(_Typography, {
    fontWeight: 700,
    style: textPrimaryColor ? {
      color: textPrimaryColor
    } : undefined
  }, geogroup !== null && highlightedEventChallenge ? t('commons.highlighted_event_card.title_joined', {
    title: highlightedEvent.title
  }) : t('commons.highlighted_event_card.title_not_joined', {
    context: mustAskAdmin ? 'must_ask' : '',
    title: highlightedEvent.title
  })), /*#__PURE__*/React.createElement(_Typography, {
    style: textPrimaryColor ? {
      color: textPrimaryColor
    } : undefined,
    variant: "body2"
  }, highlightedEvent.subtitle || (geogroup === null && hasGeogroups ? t('commons.highlighted_event_card.event_indications_description') : t('commons.highlighted_event_card.description')))), /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    flexShrink: 0,
    gap: 2,
    minWidth: 160
  }, highlightedEvent.dedicatedWebsiteUrl && /*#__PURE__*/React.createElement(Button, {
    color: "inherit",
    href: highlightedEvent.dedicatedWebsiteUrl,
    rel: "noreferrer",
    size: "medium",
    style: geogroup === null && !hasGeogroups ? primaryCtaStyle : secondaryCtaStyle,
    target: "_blank",
    variant: geogroup === null && !hasGeogroups ? secondaryColor ? 'contained' : 'outlined' : 'text'
  }, geogroup === null && !hasGeogroups ? t('commons.highlighted_event_card.actions.join') : t('commons.actions.learn_more')), geogroup && /*#__PURE__*/React.createElement(React.Fragment, null, highlightedEventChallenge ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, _extends({
    disableElevation: true,
    color: "inherit",
    size: "medium",
    style: primaryCtaStyle,
    variant: secondaryColor ? 'contained' : 'outlined'
  }, displayButtonProps), t('commons.highlighted_event_card.actions.display')), onSeeEventOffer && /*#__PURE__*/React.createElement(Button, {
    disableElevation: true,
    onClick: onSeeEventOffer,
    size: "medium",
    style: primaryCtaStyle ?? {
      backgroundColor: '#223B5B',
      color: '#fff'
    },
    variant: "contained"
  }, t('commons.highlighted_event_card.actions.see_offers'))) : notJoinedDisplayButtonProps ? /*#__PURE__*/React.createElement(Button, _extends({
    disableElevation: true,
    color: "inherit",
    size: "medium",
    style: primaryCtaStyle,
    variant: secondaryColor ? 'contained' : 'outlined'
  }, notJoinedDisplayButtonProps), t('commons.highlighted_event_card.actions.display')) : mustAskAdmin ? requestSent ? /*#__PURE__*/React.createElement(Button, {
    disableElevation: true,
    disableRipple: true,
    color: "inherit",
    size: "medium",
    startIcon: /*#__PURE__*/React.createElement(_Check, null),
    style: primaryCtaStyle,
    sx: {
      cursor: 'initial'
    },
    variant: secondaryColor ? 'contained' : 'outlined'
  }, t('commons.highlighted_event_card.actions.request_sent')) : /*#__PURE__*/React.createElement(Button, {
    disableElevation: true,
    color: "inherit",
    disabled: joiningEvent || requestSent !== false,
    onClick: handleEventAskToJoin,
    size: "medium",
    startIcon: requestSent && /*#__PURE__*/React.createElement(_Check, null),
    style: primaryCtaStyle,
    variant: secondaryColor ? 'contained' : 'outlined'
  }, t('commons.highlighted_event_card.actions.ask_to_join')) : /*#__PURE__*/React.createElement(Button, {
    disableElevation: true,
    color: "inherit",
    disabled: joiningEvent,
    onClick: onStartEventOnboarding ?? handleEventJoin,
    size: "medium",
    style: primaryCtaStyle,
    variant: secondaryColor ? 'contained' : 'outlined'
  }, t('commons.highlighted_event_card.actions.join'))))), onClose && /*#__PURE__*/React.createElement(_Tooltip, {
    title: t('commons.actions.close')
  }, /*#__PURE__*/React.createElement(_IconButton, {
    color: "inherit",
    onClick: onClose,
    size: "small",
    sx: row ? {
      alignSelf: 'flex-start',
      flexShrink: 0
    } : {
      position: 'absolute',
      top: 8,
      right: 8
    }
  }, /*#__PURE__*/React.createElement(_Close, {
    fontSize: "small"
  }))))));
}
function BottomIllustration({
  color
}) {
  return /*#__PURE__*/React.createElement("svg", {
    fill: "none",
    height: "68",
    viewBox: "0 0 220 68",
    width: "220",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M134.5 30C57.943 10.9316 42.4905 47.4401 0 68H220V0C179.5 33.5 161 36.6005 134.5 30Z",
    fill: color || defaultColor
  }));
}
function TopIllustration({
  color
}) {
  return /*#__PURE__*/React.createElement("svg", {
    fill: "none",
    height: "87",
    viewBox: "0 0 162 87",
    width: "162",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M0.0107666 87C93.2275 65.0854 65.9998 29.5 116.5 13C128.572 9.05549 148.5 16.5 161.5 5.84891e-05C143.532 5.02023e-05 13 5.05447e-05 0.00390625 0.000143366C0.00512029 48.5527 0.0105413 11.104 0.0107666 87Z",
    fill: color
  }));
}